import { lazy, Suspense, type ReactNode } from 'react'
import { ManageRedirect } from '@/routes/ManageRedirect'
import { PollRedirect } from '@/routes/PollRedirect'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RouteSkeleton } from '@/components/ui/loader'
import { RouteError } from '@/components/ui/route-error'
import { AppLayout } from '@/layouts/AppLayout'
import { PublicLayout } from '@/layouts/PublicLayout'
import { HelpLayout } from '@/layouts/HelpLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { LandingOrDashboard } from './LandingOrDashboard'
import { OrganizationSwitch } from './OrganizationSwitch'

import {
  RequireEventId,
  RequireOperator,
  RequireOffersModule,
  RequireDestinationAccess,
  RequirePresentationsAccess,
  RequireProAccess,
} from '@/routes/guards'


// Auth views are only needed on their own routes. Keep the shared auth
// provider eager, but exclude forms and the WebGL backdrop from app startup.
const AuthLayout = lazy(() =>
  import('@/layouts/AuthLayout').then((m) => ({ default: m.AuthLayout }))
)
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const SignupPage = lazy(() =>
  import('@/pages/auth/SignupPage').then((m) => ({ default: m.SignupPage }))
)
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
)
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
)
const SignupSuccessPage = lazy(() =>
  import('@/pages/auth/SignupSuccessPage').then((m) => ({ default: m.SignupSuccessPage }))
)
const VerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
)
const ConfirmEmailPage = lazy(() =>
  import('@/pages/auth/ConfirmEmailPage').then((m) => ({ default: m.ConfirmEmailPage }))
)
const VelkommenPage = lazy(() =>
  import('@/pages/auth/VelkommenPage').then((m) => ({ default: m.VelkommenPage }))
)

// ── All other pages — lazy (code split) ──
const CateringPage = lazy(() =>
  import('@/pages/CateringPage').then((m) => ({ default: m.CateringPage }))
)
const FireSafetySharePage = lazy(() =>
  import('@/pages/FireSafetySharePage').then((m) => ({ default: m.FireSafetySharePage }))
)
const NotFound = lazy(() =>
  import('@/pages/NotFound').then((m) => ({ default: m.NotFound }))
)
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage }))
)
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage }))
)
const AboutPage = lazy(() =>
  import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage }))
)
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage }))
)
const PrivacyPage = lazy(() =>
  import('@/pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
)
const TermsPage = lazy(() =>
  import('@/pages/legal/TermsPage').then((m) => ({ default: m.TermsPage }))
)
const CookiesPage = lazy(() =>
  import('@/pages/legal/CookiesPage').then((m) => ({ default: m.CookiesPage }))
)
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const EventsPage = lazy(() =>
  import('@/pages/EventsPage').then((m) => ({ default: m.EventsPage }))
)
const EventDetailPage = lazy(() =>
  import('@/pages/EventDetailPage').then((m) => ({ default: m.EventDetailPage }))
)
const CreateEventPage = lazy(() =>
  import('@/pages/CreateEventPage').then((m) => ({ default: m.CreateEventPage }))
)
const EditEventPage = lazy(() =>
  import('@/pages/EditEventPage').then((m) => ({ default: m.EditEventPage }))
)
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
)
const CalendarPage = lazy(() =>
  import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage }))
)
const SubscriptionSuccessPage = lazy(() =>
  import('@/pages/SubscriptionSuccessPage').then((m) => ({ default: m.SubscriptionSuccessPage }))
)
const OffersPage = lazy(() =>
  import('@/pages/OffersPage').then((m) => ({ default: m.OffersPage }))
)
const CreateOfferPage = lazy(() =>
  import('@/pages/CreateOfferPage').then((m) => ({ default: m.CreateOfferPage }))
)
const OfferBuilderPage = lazy(() =>
  import('@/pages/OfferBuilderPage').then((m) => ({ default: m.OfferBuilderPage }))
)
const PublicOfferViewPage = lazy(() =>
  import('@/pages/PublicOfferViewPage').then((m) => ({ default: m.PublicOfferViewPage }))
)
const OfferConfirmationPage = lazy(() =>
  import('@/pages/OfferConfirmationPage').then((m) => ({ default: m.OfferConfirmationPage }))
)
const AttendeeEventPage = lazy(() =>
  import('@/pages/AttendeeEventPage').then((m) => ({ default: m.AttendeeEventPage }))
)
const KommunikasjonPage = lazy(() =>
  import('@/pages/kommunikasjon/KommunikasjonPage').then((m) => ({ default: m.KommunikasjonPage }))
)
const MessagesPage = lazy(() =>
  import('@/pages/MessagesPage').then((m) => ({ default: m.MessagesPage }))
)
const VendorCataloguePage = lazy(() =>
  import('@/pages/VendorCataloguePage').then((m) => ({ default: m.VendorCataloguePage }))
)
const DestinationPackagesPage = lazy(() =>
  import('@/pages/DestinationPackagesPage').then((m) => ({ default: m.DestinationPackagesPage }))
)
const AcceptInvitePage = lazy(() =>
  import('@/pages/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage }))
)
const AcceptOrgInvitePage = lazy(() =>
  import('@/pages/AcceptOrgInvitePage').then((m) => ({ default: m.AcceptOrgInvitePage }))
)
const SmsOptOutPage = lazy(() =>
  import('@/pages/SmsOptOutPage').then((m) => ({ default: m.SmsOptOutPage }))
)
const PublicFormResponsePage = lazy(() =>
  import('@/pages/PublicFormResponsePage').then((m) => ({ default: m.PublicFormResponsePage }))
)
const PublicFormFillPage = lazy(() =>
  import('@/pages/PublicFormFillPage').then((m) => ({ default: m.PublicFormFillPage }))
)
const FocusedEventFormPage = lazy(() =>
  import('@/pages/FocusedEventFormPage').then((m) => ({ default: m.FocusedEventFormPage }))
)
// ── Settings — one shell, six panels (phase 2, frames 17a + 17d) ──
const SettingsLayout = lazy(() =>
  import('@/pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout }))
)
const SettingsProfilePage = lazy(() =>
  import('@/pages/settings/SettingsProfilePage').then((m) => ({ default: m.SettingsProfilePage }))
)
const SettingsPasswordPage = lazy(() =>
  import('@/pages/settings/SettingsPasswordPage').then((m) => ({ default: m.SettingsPasswordPage }))
)
const SettingsNotificationsPage = lazy(() =>
  import('@/pages/settings/SettingsNotificationsPage').then((m) => ({ default: m.SettingsNotificationsPage }))
)
const SettingsCalendarPage = lazy(() =>
  import('@/pages/settings/SettingsCalendarPage').then((m) => ({ default: m.SettingsCalendarPage }))
)
const SettingsBrandingPage = lazy(() =>
  import('@/pages/settings/SettingsBrandingPage').then((m) => ({ default: m.SettingsBrandingPage }))
)
const SettingsMessagesPage = lazy(() =>
  import('@/pages/settings/SettingsMessagesPage').then((m) => ({ default: m.SettingsMessagesPage }))
)
const SettingsBillingPage = lazy(() =>
  import('@/pages/settings/SettingsBillingPage').then((m) => ({ default: m.SettingsBillingPage }))
)
const SettingsOrganizationPage = lazy(() =>
  import('@/pages/settings/SettingsOrganizationPage').then((m) => ({ default: m.SettingsOrganizationPage }))
)
const PresentationsPage = lazy(() =>
  import('@/pages/PresentationsPage').then((m) => ({ default: m.PresentationsPage }))
)
const PresentationBuilderPage = lazy(() =>
  import('@/pages/PresentationBuilderPage').then((m) => ({ default: m.PresentationBuilderPage }))
)
const PublicPresentationPage = lazy(() =>
  import('@/pages/PublicPresentationPage').then((m) => ({ default: m.PublicPresentationPage }))
)

// ── Hjelpesenteret ──
// Three routes, one chunk. The corpus is imported eagerly inside the registry,
// so splitting these apart would only add round trips between a reader and the
// article they are already looking for.
const HelpIndexPage = lazy(() =>
  import('@/pages/help/HelpIndexPage').then((m) => ({ default: m.HelpIndexPage }))
)
const HelpCategoryPage = lazy(() =>
  import('@/pages/help/HelpCategoryPage').then((m) => ({ default: m.HelpCategoryPage }))
)
const HelpArticlePage = lazy(() =>
  import('@/pages/help/HelpArticlePage').then((m) => ({ default: m.HelpArticlePage }))
)

/* No delay before the placeholder (24.10.2026): a 250 ms blank between two placeholders was one of
   the flickers in David's recording. The placeholder is the destination's own shape, so showing it
   at once costs nothing when the code arrives fast. */
function LazyFallback() {
  return <RouteSkeleton />
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>
}


export const router = createBrowserRouter([
  // ── Landing page — standalone, no layout wrapper ──
  {
    path: '/',
    errorElement: <RouteError shell="public" />,
    // En innlogget bruker skal aldri møte salgssiden. Se LandingOrDashboard for
    // hvorfor dette står HER og ikke som en parameter på hver innloggingslenke.
    element: <LandingOrDashboard landing={<Lazy><LandingPage /></Lazy>} />,
  },

// ── Public pages ──
  {
    errorElement: <RouteError shell="public" />,
    element: <PublicLayout />,
    children: [
      { path: '/pricing', element: <Lazy><PricingPage /></Lazy> },
      { path: '/about', element: <Lazy><AboutPage /></Lazy> },
      { path: '/contact', element: <Lazy><ContactPage /></Lazy> },
      { path: '/privacy', element: <Lazy><PrivacyPage /></Lazy> },
      { path: '/terms', element: <Lazy><TermsPage /></Lazy> },
      { path: '/cookies', element: <Lazy><CookiesPage /></Lazy> },
    ],
  },

  // ── Hjelpesenteret ──
  // Its own shell: marketing header and footer, app reading surface. See
  // `layouts/HelpLayout.tsx` for why it is not `PublicLayout`.
  //
  // Public by design and NOT behind ProtectedRoute: the two articles with the
  // widest audience are written for someone who has no account and cannot get
  // one (a guest answering an invitation from an SMS). A help centre they have
  // to log in to read is a help centre that does not serve them.
  //
  // `/help` is an alias, not a second implementation. English readers guess it,
  // and it is one line here against a whole parallel route tree.
  {
    errorElement: <RouteError shell="public" />,
    element: <HelpLayout />,
    children: [
      { path: '/hjelp', element: <Lazy><HelpIndexPage /></Lazy> },
      { path: '/hjelp/:category', element: <Lazy><HelpCategoryPage /></Lazy> },
      { path: '/hjelp/:category/:slug', element: <Lazy><HelpArticlePage /></Lazy> },
    ],
  },
  { path: '/help', element: <Navigate to="/hjelp" replace /> },

  // ── Auth pages ──
  {
    // 21d: this tree had no errorElement, so a throw on /login was a white
    // screen. Public variant — an unauthenticated visitor must never be
    // offered the dashboard.
    errorElement: <RouteError shell="public" />,
    element: <Lazy><AuthLayout /></Lazy>,
    children: [
      { path: '/login', element: <Lazy><LoginPage /></Lazy> },
      { path: '/signup', element: <Lazy><SignupPage /></Lazy> },
      { path: '/forgot-password', element: <Lazy><ForgotPasswordPage /></Lazy> },
      { path: '/reset-password', element: <Lazy><ResetPasswordPage /></Lazy> },
      { path: '/signup-success', element: <Lazy><SignupSuccessPage /></Lazy> },
      { path: '/verify-email', element: <Lazy><VerifyEmailPage /></Lazy> },
      { path: '/auth/confirm-email', element: <Lazy><ConfirmEmailPage /></Lazy> },
      // Magic-link landing: one-click token gate + self-serve fresh link.
      // NOT /invite/:token — that path is the admin-role invite (AcceptInvitePage).
      { path: '/velkommen', element: <Lazy><VelkommenPage /></Lazy> },
    ],
  },

  // ── Public event/offer pages (no auth required) ──
  {
    // 21d + 22c: the eight token routes are opened from an SMS by someone with
    // no account, and they had no boundary either. 22c ranks them P0 — «Én
    // ekstern bruker per invitasjon — feilen er offentlig.»
    errorElement: <RouteError shell="public" />,
    children: [
      {
        // Optional trailing slug for pretty URLs — Notion/Linear style.
        // The invitationToken is the source-of-truth lookup; the slug is
        // decorative and ignored by the page. Both forms work:
        //   /event/public/<token>
        //   /event/public/<token>/<event-name-slug>
        path: '/event/public/:invitationToken/:slug?',
        element: <Lazy><AttendeeEventPage /></Lazy>,
      },
      {
        path: '/servering/:token',
        element: <Lazy><CateringPage /></Lazy>,
      },
      {
        // The venue's live fire-safety list: attending names and the headcount, nothing else.
        path: '/brannsikkerhet/:token',
        element: <Lazy><FireSafetySharePage /></Lazy>,
      },
      {
        path: '/offer/:shareToken',
        element: <Lazy><PublicOfferViewPage /></Lazy>,
      },
      {
        path: '/confirm-offer/:token',
        element: <Lazy><OfferConfirmationPage /></Lazy>,
      },
      {
        // Retired standalone voting page; voting lives on the event itself.
        path: '/poll/:eventId/:token',
        element: <PollRedirect />,
      },
      {
        path: '/invite/:token',
        element: <Lazy><AcceptInvitePage /></Lazy>,
      },
      {
        path: '/org-invite/:token',
        element: <Lazy><AcceptOrgInvitePage /></Lazy>,
      },
      {
        // «Avmeld: …/r/<kode>», appended by the sender to every marketing SMS.
        path: '/r/:code',
        element: <Lazy><SmsOptOutPage /></Lazy>,
      },
      {
        path: '/form/respond/:token',
        element: <Lazy><PublicFormResponsePage /></Lazy>,
      },
      {
        path: '/form/f/:token',
        element: <Lazy><PublicFormFillPage /></Lazy>,
      },
      {
        path: '/p/:token',
        element: <Lazy><PublicPresentationPage /></Lazy>,
      },
    ],
  },

  // Personal SMS destination: authenticated, event-branded content without
  // full app navigation while the participant is answering.
  {
    path: '/event/:id/form/:formId',
    errorElement: <RouteError shell="app" />,
    element: (
      <ProtectedRoute>
        <RequireEventId><Lazy><FocusedEventFormPage /></Lazy></RequireEventId>
      </ProtectedRoute>
    ),
  },

  // Tenant mutation occurs only after the prior route's draft guard proceeds.
  // Keep AppLayout unmounted until a full reload has cleared tenant caches.
  {
    path: '/organization/switch/:orgId',
    errorElement: <RouteError shell="app" />,
    element: <ProtectedRoute><OrganizationSwitch /></ProtectedRoute>,
  },

  // ── Authenticated pages ──
  {
    errorElement: <RouteError shell="app" />,
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/dashboard', element: <Lazy><DashboardPage /></Lazy> },
      { path: '/events', element: <Lazy><EventsPage /></Lazy> },
      { path: '/offers', element: <RequireOperator tool="offersRole"><RequireOffersModule><Lazy><OffersPage /></Lazy></RequireOffersModule></RequireOperator> },
      { path: '/offers/create', element: <RequireOperator tool="offersRole"><RequireOffersModule><Lazy><CreateOfferPage /></Lazy></RequireOffersModule></RequireOperator> },
      { path: '/destinations', element: <RequireDestinationAccess><Lazy><DestinationPackagesPage /></Lazy></RequireDestinationAccess> },
      { path: '/catalogue', element: <RequireOperator tool="offersRole"><RequireOffersModule><RequireProAccess><Lazy><VendorCataloguePage /></Lazy></RequireProAccess></RequireOffersModule></RequireOperator> },
      { path: '/presentations', element: <RequirePresentationsAccess><Lazy><PresentationsPage /></Lazy></RequirePresentationsAccess> },
      { path: '/presentations/:id', element: <RequirePresentationsAccess><Lazy><PresentationBuilderPage /></Lazy></RequirePresentationsAccess> },
      // `RequireEventId` wraps all four `/event/:id*` routes: an id that cannot
      // be a uuid is a 404 before any fetch, not a retryable data error (F-08).
      { path: '/event/:id', element: <RequireEventId><Lazy><EventDetailPage /></Lazy></RequireEventId> },
      { path: '/event/:id/edit', element: <RequireEventId><Lazy><EditEventPage /></Lazy></RequireEventId> },
      // `/event/:id/manage` is retired. The redirect is PERMANENT and carries
      // the tab across: the links people hold are `?g=<stack>&t=<tab>`, `g`
      // dies with the stacks, and `t` survives because the tab does. See
      // handoff/plan/REDIRECT-ARRANGEMENTSFLATEN.md for the two slugs whose
      // meaning changed and for why an inaccessible tab falls to Oversikt
      // rather than to a dead end.
      { path: '/event/:id/manage', element: <RequireEventId><ManageRedirect /></RequireEventId> },
      {
        path: '/event/:id/offer/edit',
        element: <RequireEventId><Lazy><OfferBuilderPage /></Lazy></RequireEventId>,
      },
      { path: '/create-event', element: <Lazy><CreateEventPage /></Lazy> },
      { path: '/calendar', element: <Lazy><CalendarPage /></Lazy> },
      { path: '/kommunikasjon', element: <RequireOperator><Lazy><KommunikasjonPage /></Lazy></RequireOperator> },
      { path: '/messages', element: <Lazy><MessagesPage /></Lazy> },
      // Før '/messages/:eventId', slik at «dm» aldri kan leses som en eventId.
      // React Router rangerer statiske segmenter over dynamiske, så dette er
      // belte og seler — men rekkefølgen er også dokumentasjonen.
      { path: '/messages/dm/:conversationId', element: <Lazy><MessagesPage /></Lazy> },
      { path: '/messages/:eventId', element: <Lazy><MessagesPage /></Lazy> },
      { path: '/notifications', element: <Lazy><NotificationsPage /></Lazy> },
      { path: '/subscription-success', element: <Lazy><SubscriptionSuccessPage /></Lazy> },

      // ── Settings — phase 2, frames 17a + 17d ──
      // One shell with a submenu. The index renders the profile panel rather
      // than redirecting: at lg+ the panel sits beside the menu, and below lg
      // `/settings` IS the menu, so a redirect would rob a phone of the page
      // this phase exists to create.
      {
        path: '/settings',
        element: <Lazy><SettingsLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><SettingsProfilePage /></Lazy> },
          { path: '/settings/profile', element: <Lazy><SettingsProfilePage /></Lazy> },
          { path: '/settings/password', element: <Lazy><SettingsPasswordPage /></Lazy> },
          { path: '/settings/notifications', element: <Lazy><SettingsNotificationsPage /></Lazy> },
          { path: '/settings/calendar', element: <Lazy><SettingsCalendarPage /></Lazy> },
          { path: '/settings/branding', element: <Lazy><SettingsBrandingPage /></Lazy> },
          { path: '/settings/messages', element: <Lazy><SettingsMessagesPage /></Lazy> },
          { path: '/settings/billing', element: <Lazy><SettingsBillingPage /></Lazy> },
          { path: '/settings/organization', element: <Lazy><SettingsOrganizationPage /></Lazy> },
        ],
      },

      // ── Legacy settings paths — redirects, never 404s ──
      // These are in sent email, calendar invites and browser histories, and
      // `/profile/change-password` is still what ProtectedRoute's forced
      // password change used to target. Removing them would break links we do
      // not control.
      { path: '/profile', element: <Navigate to="/settings/profile" replace /> },
      { path: '/profile/change-password', element: <Navigate to="/settings/password" replace /> },
      { path: '/calendar-settings', element: <Navigate to="/settings/calendar" replace /> },
    ],
  },

  // ── Catch-all ──
  { path: '*', element: <Lazy><NotFound /></Lazy> },
])
